/**
 * Aktenraum: welcher persistierte ARKO-Thread zur gebundenen Akte gehört.
 * Rein, ohne React — Hard-Refresh darf keinen leeren Neu-Thread über einen Verlauf mit Messages setzen.
 */

export const AKTE_HANDOFF_MAX_CHARS = 1200;
export const AKTE_HANDOFF_FIELD_CHARS = 280;
/** Unter dem Handoff-Banner — kein neuer Chip. Anwalt hakelt den nächsten Block selbst. */
export const AKTE_HANDOFF_DOCS_HINT = 'Docs für diesen Arbeitsblock anhaken';

/** Curator-Anweisung für den bestehenden Vorschau-Pfad — kein zweiter Speicher. */
export const AKTE_HANDOFF_CURATOR_ANWEISUNG =
  'Nur aus diesem Chat und dieser Akte, nichts erfinden. Kurzer Handoff, kein Essay. Struktur exakt:\n'
  + 'Anliegen:\nFeststeht:\nOffen:\nLetzter Entwurf in der Akte:';

const HANDOFF_UNKNOWN = '(nicht in Chat/Akte genannt)';
const LEX_STAMP_RE = /\[LEX:[^\]]+\]/i;
const STAMPED_NORM_RE = /\b[A-Z]{2,12}-\d+[a-z0-9]*(?:-\d+)?\b/;
const LAWYER_CONFIRM_RE = /anwaltlich best(?:[aä]|ae)tigt|vom anwalt best(?:[aä]|ae)tigt/i;

export const PORTAL_SAVE_OFFEN_HINT =
  'Das liegt als Offen. Es ist nicht gestempelt und nicht bestätigt.';
export const PORTAL_SAVE_FESTSTEHT_HINT = 'Das liegt als Feststeht.';

/** Feststeht nur mit LEX-Stempel plus Norm-ID oder mit Anwalts-Bestätigung. */
export function textIsStampedOrConfirmed(text: string): boolean {
  const raw = text.replace(/\s+/g, ' ').trim();
  if (!raw || raw === HANDOFF_UNKNOWN) return false;
  if (LAWYER_CONFIRM_RE.test(raw)) return true;
  return LEX_STAMP_RE.test(raw) && STAMPED_NORM_RE.test(raw);
}

/** Sichtbares Ergebnis von „In Akte speichern“ und „Ins Gedächtnis“. */
export function savedChatStand(text: string): 'Offen' | 'Feststeht' {
  return textIsStampedOrConfirmed(text) ? 'Feststeht' : 'Offen';
}

export function formatSavedChatStatus(text: string): string {
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  const stand = savedChatStand(raw);
  return raw ? `${stand}: ${raw}` : stand;
}

/** Akten-Datei behält die Absätze. Die erste Zeile ist Offen oder Feststeht. */
export function prefixSavedChatStatus(text: string): string {
  const raw = String(text || '').replace(/\r\n/g, '\n').trim();
  const stand = savedChatStand(raw);
  return raw ? `${stand}:\n${raw}` : stand;
}

export interface BindableThread {
  id: string;
  akteId?: string | null;
  messages: Array<{ role?: string }>;
  updatedAt: number;
  /** Sichtbarer Arbeitsstand für den nächsten Chat an derselben Akte (kein Verlauf). */
  akteHandoff?: string | null;
  /** Alter Thread nach Weiterarbeiten — nicht mehr als Bind-Ziel. */
  handedOff?: boolean;
}

export type BindAkteDecision =
  | { action: 'keep'; threadId: string }
  | { action: 'switch'; threadId: string; fromArchive: boolean }
  | { action: 'attach'; threadId: string }
  | { action: 'create' };

export function threadHasUserMessages(thread: BindableThread | null | undefined): boolean {
  return Boolean(thread?.messages.some((m) => m.role === 'user'));
}

/** Jede gespeicherte Nachricht zählt — auch Bot-only (Hintergrundanalyse ohne User-Turn). */
export function threadHasMessages(thread: BindableThread | null | undefined): boolean {
  return Boolean(thread?.messages.length);
}

export interface PersistableWorkspace {
  threads: Record<string, BindableThread>;
  archived?: Record<string, BindableThread>;
}

export function workspaceHasUserMessages(state: PersistableWorkspace | null | undefined): boolean {
  if (!state) return false;
  const all = [...Object.values(state.threads), ...Object.values(state.archived ?? {})];
  return all.some(threadHasUserMessages);
}

export function workspaceHasMessages(state: PersistableWorkspace | null | undefined): boolean {
  if (!state) return false;
  const all = [...Object.values(state.threads), ...Object.values(state.archived ?? {})];
  return all.some(threadHasMessages);
}

/**
 * View-Wechsel mountet ARKO zweimal (Chat-View vs. Split). Der neue Mount lädt
 * oft noch den alten Snapshot, bevor der Unmount flusht. Ein leerer Neu-Thread
 * darf deshalb keinen Verlauf mit User-Messages überschreiben.
 */
export function shouldPersistWorkspace(
  incoming: PersistableWorkspace,
  stored: PersistableWorkspace | null,
): boolean {
  if (!stored) return true;
  if (!workspaceHasUserMessages(incoming) && workspaceHasUserMessages(stored)) {
    return false;
  }
  if (!workspaceHasMessages(incoming) && workspaceHasMessages(stored)) {
    return false;
  }
  return true;
}

function rankThread(thread: BindableThread): number {
  return (threadHasUserMessages(thread) ? 1_000_000_000_000 : 0)
    + (threadHasMessages(thread) ? 1_000_000_000 : 0)
    + thread.updatedAt;
}

/** Gleicher Schlüssel wie der ARKO-Workspace. Nur die Chat-ID, keine Nachricht. */
export const ARKO_THREAD_STORAGE_PREFIX = 'arkorbit_portal_arko_threads_v1__';

export function peekAkteChatId(customerId: string, akteId: string): string | null {
  if (typeof localStorage === 'undefined') return null;
  const cid = customerId.trim();
  const aid = akteId.trim();
  if (!cid || !aid) return null;
  try {
    const raw = localStorage.getItem(ARKO_THREAD_STORAGE_PREFIX + cid);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      threads?: Record<string, unknown>;
      archived?: Record<string, unknown>;
    };
    const asThread = (value: unknown, fallbackId: string): BindableThread | null => {
      if (!value || typeof value !== 'object') return null;
      const row = value as Record<string, unknown>;
      const id = typeof row.id === 'string' && row.id.trim() ? row.id.trim() : fallbackId;
      if (!id) return null;
      const messages = Array.isArray(row.messages)
        ? row.messages.filter((item): item is { role?: string } => Boolean(item) && typeof item === 'object')
        : [];
      return {
        id,
        akteId: typeof row.akteId === 'string' ? row.akteId : '',
        messages,
        updatedAt: typeof row.updatedAt === 'number' ? row.updatedAt : 0,
        handedOff: row.handedOff === true,
      };
    };
    const threads: Record<string, BindableThread> = {};
    const archived: Record<string, BindableThread> = {};
    for (const [key, value] of Object.entries(parsed.threads || {})) {
      const thread = asThread(value, key);
      if (thread) threads[thread.id] = thread;
    }
    for (const [key, value] of Object.entries(parsed.archived || {})) {
      const thread = asThread(value, key);
      if (thread) archived[thread.id] = thread;
    }
    return findBestThreadForAkte(threads, archived, aid)?.id ?? null;
  } catch {
    return null;
  }
}

export function findBestThreadForAkte<T extends BindableThread>(
  threads: Record<string, T>,
  archived: Record<string, T>,
  akteId: string,
): T | null {
  const id = akteId.trim();
  if (!id) return null;
  const matches = [...Object.values(threads), ...Object.values(archived)]
    .filter((t) => (t.akteId || '').trim() === id);
  if (matches.length === 0) return null;
  const live = matches.filter((t) => !t.handedOff);
  const pool = live.length > 0 ? live : matches;
  return pool.slice().sort((a, b) => rankThread(b) - rankThread(a))[0];
}

function clipHandoffField(raw: string, maxChars: number): string {
  const compact = raw.replace(/\s+/g, ' ').trim();
  if (!compact) return '';
  if (compact.length <= maxChars) return compact;
  return `${compact.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
}

export function capAkteHandoff(text: string, maxChars = AKTE_HANDOFF_MAX_CHARS): string {
  return clipHandoffField(text, maxChars);
}

export interface AkteHandoffDraft {
  anliegen: string;
  feststeht: string;
  offen: string;
  letzterEntwurf: string;
}

/** Strukturierter Handoff — nur übergebene Felder, nichts erfinden. */
export function formatAkteHandoff(
  draft: AkteHandoffDraft,
  maxChars = AKTE_HANDOFF_MAX_CHARS,
): string {
  const line = (label: string, value: string) =>
    `${label}: ${clipHandoffField(value, AKTE_HANDOFF_FIELD_CHARS) || HANDOFF_UNKNOWN}`;
  return capAkteHandoff(
    [
      line('Anliegen', draft.anliegen),
      line('Feststeht', draft.feststeht),
      line('Offen', draft.offen),
      line('Letzter Entwurf in der Akte', draft.letzterEntwurf),
    ].join('\n'),
    maxChars,
  );
}

function splitBotForFeststeht(lastBotText: string): { feststeht: string; offen: string } {
  const raw = lastBotText.replace(/\s+/g, ' ').trim();
  if (!raw) return { feststeht: '', offen: '' };
  if (textIsStampedOrConfirmed(raw)) return { feststeht: raw, offen: '' };
  return { feststeht: '', offen: raw };
}

/**
 * Curator- oder Freitext darf Feststeht nicht vorfüllen.
 * Ungestempelter Feststeht-Inhalt wandert nach Offen.
 */
export function gateHandoffFeststeht(text: string): string {
  const raw = String(text || '').replace(/\r\n/g, '\n').trim();
  if (!raw) return raw;
  const fields = {
    anliegen: '',
    feststeht: '',
    offen: '',
    letzterEntwurf: '',
  };
  let structured = false;
  for (const line of raw.split('\n')) {
    const anliegen = line.match(/^Anliegen:\s*(.*)$/);
    const feststeht = line.match(/^Feststeht:\s*(.*)$/);
    const offen = line.match(/^Offen:\s*(.*)$/);
    const entwurf = line.match(/^Letzter Entwurf in der Akte:\s*(.*)$/);
    if (anliegen) {
      structured = true;
      fields.anliegen = anliegen[1].trim();
    } else if (feststeht) {
      structured = true;
      fields.feststeht = feststeht[1].trim();
    } else if (offen) {
      structured = true;
      fields.offen = offen[1].trim();
    } else if (entwurf) {
      structured = true;
      fields.letzterEntwurf = entwurf[1].trim();
    }
  }
  if (!structured) {
    return formatAkteHandoff({
      anliegen: '',
      feststeht: '',
      offen: raw,
      letzterEntwurf: '',
    });
  }
  const festRaw = fields.feststeht === HANDOFF_UNKNOWN ? '' : fields.feststeht;
  const offenRaw = fields.offen === HANDOFF_UNKNOWN ? '' : fields.offen;
  const feststeht = textIsStampedOrConfirmed(festRaw) ? festRaw : '';
  const moved = festRaw && !feststeht ? festRaw : '';
  const offen = [offenRaw, moved].filter(Boolean).join(' ');
  return formatAkteHandoff({
    anliegen: fields.anliegen === HANDOFF_UNKNOWN ? '' : fields.anliegen,
    feststeht,
    offen,
    letzterEntwurf: fields.letzterEntwurf === HANDOFF_UNKNOWN ? '' : fields.letzterEntwurf,
  });
}

export function buildLocalAkteHandoff(args: {
  lastUserText?: string | null;
  lastBotText?: string | null;
  selectedDocTitles?: string[] | null;
  /** Vom Anwalt bestätigter Text — nicht der Bot- oder Curator-Entwurf. */
  confirmedText?: string | null;
}): string {
  const titles = (args.selectedDocTitles || []).map((t) => t.trim()).filter(Boolean);
  const split = splitBotForFeststeht(args.lastBotText || '');
  const confirmed = String(args.confirmedText || '').trim();
  return formatAkteHandoff({
    anliegen: args.lastUserText || '',
    feststeht: confirmed || split.feststeht,
    offen: split.offen,
    letzterEntwurf: titles.length ? titles.join(', ') : '',
  });
}

export function threadIsFreshHandoff(thread: BindableThread | null | undefined): boolean {
  return Boolean(thread?.akteHandoff?.trim()) && !threadHasUserMessages(thread);
}

/** Fokus-Doc nicht still anhaken — nach Weiterarbeiten leert der Anwalt die Haken selbst. */
export function shouldAutoSelectFocusDocument(
  thread: BindableThread | null | undefined,
): boolean {
  return !threadIsFreshHandoff(thread);
}

/**
 * Bindungsentscheidung beim Mount / Akte-Wechsel.
 * Ungebundener Chat mit Verlauf wird an die Akte gehängt statt durch einen leeren Thread ersetzt.
 */
export function decideBindAkteThread(args: {
  akteId: string;
  activeId: string;
  threads: Record<string, BindableThread>;
  archived: Record<string, BindableThread>;
}): BindAkteDecision {
  const akteId = args.akteId.trim();
  if (!akteId) return { action: 'create' };

  const current = args.threads[args.activeId];
  const currentAkteId = (current?.akteId || '').trim();
  const best = findBestThreadForAkte(args.threads, args.archived, akteId);

  if (current && currentAkteId === akteId) {
    // Weiterarbeiten: leerer neuer Thread mit Handoff darf den alten Verlauf nicht erben.
    if (threadIsFreshHandoff(current)) {
      return { action: 'keep', threadId: current.id };
    }
    if (
      best
      && best.id !== current.id
      && threadHasUserMessages(best)
      && !threadHasMessages(current)
    ) {
      return {
        action: 'switch',
        threadId: best.id,
        fromArchive: Boolean(args.archived[best.id]),
      };
    }
    return { action: 'keep', threadId: current.id };
  }

  if (current && !currentAkteId && threadHasUserMessages(current)) {
    return { action: 'attach', threadId: current.id };
  }

  if (best) {
    return {
      action: 'switch',
      threadId: best.id,
      fromArchive: Boolean(args.archived[best.id]),
    };
  }

  if (current && !currentAkteId) {
    return { action: 'attach', threadId: current.id };
  }

  return { action: 'create' };
}

/**
 * Plus-Button: neuer Thread ohne Akte-Erbe.
 * Parent muss boundAkte auf null setzen (localStorage), sonst hängt F5/Akte-öffnen
 * den leeren Thread wieder an (decideBindAkteThread attach).
 * User-Override hält den Kontext-Toggle aus — Hydrate/Default darf ihn nicht sofort wieder einschalten.
 */
export function plusChatUnboundFields(): {
  akteId: undefined;
  documentContextAkteNcPath: null;
  documentContextEnabled: false;
  documentContextUserOverride: true;
  akteHandoff: undefined;
  handedOff: undefined;
} {
  return {
    akteId: undefined,
    documentContextAkteNcPath: null,
    documentContextEnabled: false,
    documentContextUserOverride: true,
    akteHandoff: undefined,
    handedOff: undefined,
  };
}

/**
 * Weiterarbeiten: neuer Thread an derselben Akte, leerer Verlauf, Handoff als Kontextzeile.
 * Parent darf boundAkte NICHT auf null setzen (Gegensatz zu Plus).
 * Dokument-Haken leer — Auszug-Cache bleibt im Parent, kein Re-OCR.
 */
export function continueAkteChatFields(akteId: string, handoff: string): {
  akteId: string;
  akteHandoff: string;
  handedOff: undefined;
  documentContextEnabled: true;
  documentContextUserOverride: true;
  selectedDocumentIds: [];
} {
  const id = akteId.trim();
  return {
    akteId: id,
    akteHandoff: capAkteHandoff(handoff),
    handedOff: undefined,
    documentContextEnabled: true,
    documentContextUserOverride: true,
    selectedDocumentIds: [],
  };
}

/** Thread mit Akte (Akten-Spalte) vs. normaler Chat. Whitespace zaehlt nicht als Bindung. */
export function threadIsAkteBound(thread: { akteId?: string | null } | null | undefined): boolean {
  return Boolean(thread?.akteId?.trim());
}

/**
 * Aktive Thread-Liste in zwei Gruppen — Reihenfolge innerhalb jeder Gruppe bleibt.
 * Plus (`plusChatUnboundFields`) landet in `chats`; Akte-oeffnen in `akten`.
 */
export function partitionArkoThreads<T extends { akteId?: string | null }>(
  threads: T[],
): { chats: T[]; akten: T[] } {
  const chats: T[] = [];
  const akten: T[] = [];
  for (const thread of threads) {
    if (threadIsAkteBound(thread)) akten.push(thread);
    else chats.push(thread);
  }
  return { chats, akten };
}

export function resolveDocumentContextEnabled(
  thread: { documentContextEnabled?: boolean; documentContextUserOverride?: boolean },
  defaultEnabled: boolean,
  docAvailable: boolean,
  selectedContextAvailable = false,
  akteBound = false,
): boolean {
  if (!docAvailable && !selectedContextAvailable && !akteBound) return false;
  if (thread.documentContextUserOverride && thread.documentContextEnabled !== undefined) {
    return thread.documentContextEnabled;
  }
  return defaultEnabled || selectedContextAvailable || akteBound;
}

/**
 * Akte-Kontext-Switch: Aus = unbinden (null), Ein = aktuelle / letzte / Fallback-Akte.
 * Gleicher Pfad wie Dropdown „Keine Akte gebunden“ — kein zweites Persist-Feld.
 */
export function nextAkteContextBindId(args: {
  enabled: boolean;
  boundAkteId?: string | null;
  lastBoundAkteId?: string | null;
  fallbackAkteId?: string | null;
}): string | null {
  if (!args.enabled) return null;
  const bound = (args.boundAkteId || '').trim();
  if (bound) return bound;
  const last = (args.lastBoundAkteId || '').trim();
  if (last) return last;
  const fallback = (args.fallbackAkteId || '').trim();
  return fallback || null;
}
